import { Flame, Shield, Snowflake } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStreaks } from '@/hooks/useStreaks';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface StreakBadgeProps {
  showDetails?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export function StreakBadge({ showDetails = false, size = 'md' }: StreakBadgeProps) {
  const { streak, getCurrentMultiplier, getNextMilestone, loading } = useStreaks();

  if (loading || !streak) {
    return null;
  }

  const multiplier = getCurrentMultiplier();
  const nextMilestone = getNextMilestone();
  const isProtected = streak.streak_protected_until && 
    new Date(streak.streak_protected_until) >= new Date();
  const hasFreezes = streak.freezes_remaining > 0;

  const sizeClasses = {
    sm: 'text-xs px-2 py-1 gap-1',
    md: 'text-sm px-3 py-1.5 gap-1.5',
    lg: 'text-base px-4 py-2 gap-2',
  };

  const iconSizes = {
    sm: 12,
    md: 16,
    lg: 20,
  };

  const getFlameColor = () => {
    if (streak.current_streak >= 100) return 'text-purple-500';
    if (streak.current_streak >= 30) return 'text-blue-500';
    if (streak.current_streak >= 7) return 'text-orange-500';
    return 'text-yellow-500';
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className={`inline-flex items-center rounded-full bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 font-bold ${sizeClasses[size]}`}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={streak.current_streak}
              initial={{ scale: 1.5, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative"
            >
              <Flame 
                size={iconSizes[size]} 
                className={`${getFlameColor()} fill-current`}
              />
              {streak.current_streak >= 7 && (
                <motion.div
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{ 
                    duration: 1.5, 
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 bg-orange-500/30 rounded-full blur-sm"
                />
              )}
            </motion.div>
          </AnimatePresence>
          
          <span className="text-foreground tabular-nums">
            {streak.current_streak}
          </span>
          
          {multiplier > 1 && (
            <span className="text-orange-500 text-[10px] font-bold uppercase">
              {multiplier}x
            </span>
          )}
          
          {isProtected && (
            <Shield size={iconSizes[size] - 4} className="text-blue-500" />
          )}
          
          {hasFreezes && !isProtected && (
            <Snowflake size={iconSizes[size] - 4} className="text-cyan-400" />
          )}
        </motion.div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <div className="space-y-2 text-sm">
          <div className="font-bold flex items-center gap-2">
            <Flame size={16} className="text-orange-500" />
            {streak.current_streak} Day Streak
          </div>
          
          {multiplier > 1 && (
            <p className="text-muted-foreground">
              🎯 Earning <span className="text-orange-500 font-bold">{multiplier}x</span> bonus points!
            </p>
          )}
          
          {nextMilestone && (
            <p className="text-muted-foreground">
              📈 {nextMilestone.days - streak.current_streak} more days to unlock{' '}
              <span className="font-bold">{nextMilestone.name}</span> ({nextMilestone.multiplier}x bonus)
            </p>
          )}
          
          <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t border-border">
            <span>🏆 Best: {streak.longest_streak} days</span>
            {hasFreezes && <span>❄️ {streak.freezes_remaining} freeze(s)</span>}
          </div>
          
          {isProtected && (
            <p className="text-blue-500 text-xs">
              🛡️ Protected until {new Date(streak.streak_protected_until!).toLocaleDateString()}
            </p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
